/**
 * التقاط معرّف نقرة Google Ads (`gclid`) وحفظه ٩٠ يوماً.
 *
 * لماذا: الدفع يحدث على checkout شوبيفاي، لذا لا نستطيع ربط الطلب بنقرة
 * الإعلان من موقعنا وقت الشراء. نحفظ المعرّف عند وصول الزائر، ثمّ نمرّره
 * كسمة سلّة → note_attributes → عمود `gclid` في جدول `orders`.
 * بعد تأكيد التسليم نرفع التحويل إلى Google Ads (دقّة ١٠٠٪ مع طلبات COD).
 *
 * `gbraid`/`wbraid` بديلان يستخدمهما جوجل على iOS عند غياب gclid.
 */

import { hasAdsConsent } from "@/lib/cookie-consent";

const STORAGE_KEY = "lecmo:ads_click";
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // ٩٠ يوماً — نافذة التحويل القصوى في Google Ads

/** مفاتيح سمات السلّة التي يملكها هذا المصدر (تُنظَّف عند سحب الموافقة). */
export const ADS_CLICK_ATTR_KEYS = [
  "_gclid",
  "_gbraid",
  "_wbraid",
  "_ads_click_at",
] as const;

export interface AdsClickId {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  /** ISO timestamp للنقرة — Google Ads يطلبه مع كلّ تحويل مرفوع */
  clickAt: string;
  expires: number;
}

function isValidId(value: string | null): value is string {
  if (!value) return false;
  const v = value.trim();
  return v.length > 0 && v.length <= 512;
}

/** يحذف المعرّف المحفوظ — يُستدعى عند سحب موافقة الإعلانات. */
export function clearAdsClickId(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* التخزين محجوب */
  }
}

/** يقرأ المعرّف المحفوظ (أو null إن انتهت صلاحيّته أو سُحبت الموافقة). */
export function getAdsClickId(): AdsClickId | null {
  if (typeof window === "undefined") return null;
  // سحب موافقة الإعلانات يُبطل المعرّف المحفوظ فوراً ويُنظّفه.
  if (!hasAdsConsent()) {
    clearAdsClickId();
    return null;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdsClickId;
    if (!parsed?.expires || parsed.expires < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}


/**
 * يقرأ الـURL الحاليّ ويحفظ أيّ معرّف نقرة موجود.
 * نقرة أحدث تحلّ محلّ الأقدم (last-click كما يحتسبها جوجل).
 */
export function captureAdsClickId(search?: string): AdsClickId | null {
  if (typeof window === "undefined") return null;
  // ممنوع الحفظ قبل موافقة الإعلانات — ولا نُبقي قيمة سابقة بعد السحب.
  if (!hasAdsConsent()) {
    clearAdsClickId();
    return null;
  }
  try {
    const params = new URLSearchParams(search ?? window.location.search);
    const gclid = params.get("gclid");
    const gbraid = params.get("gbraid");
    const wbraid = params.get("wbraid");
    if (!isValidId(gclid) && !isValidId(gbraid) && !isValidId(wbraid)) {
      return getAdsClickId();
    }

    const next: AdsClickId = {
      ...(isValidId(gclid) ? { gclid: gclid.trim() } : {}),
      ...(isValidId(gbraid) ? { gbraid: gbraid.trim() } : {}),
      ...(isValidId(wbraid) ? { wbraid: wbraid.trim() } : {}),
      clickAt: new Date().toISOString(),
      expires: Date.now() + TTL_MS,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
  } catch {
    return null;
  }
}

/**
 * سمات السلّة الخاصّة بنقرة الإعلان (بادئة `_` = مخفيّة عن العميل في شوبيفاي).
 */
export function buildAdsClickAttributes(): Array<{ key: string; value: string }> {
  const click = getAdsClickId();
  if (!click) return [];
  const attrs: Array<{ key: string; value: string }> = [];
  if (click.gclid) attrs.push({ key: "_gclid", value: click.gclid });
  if (click.gbraid) attrs.push({ key: "_gbraid", value: click.gbraid });
  if (click.wbraid) attrs.push({ key: "_wbraid", value: click.wbraid });
  if (attrs.length > 0) attrs.push({ key: "_ads_click_at", value: click.clickAt });
  return attrs;
}
